import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Skeleton,
  Text,
  Flex,
  Badge,
  SimpleGrid,
  Stack,
  Divider,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
  TagLabel,
  Tooltip,
  Portal,
} from "@chakra-ui/react";
import { toast as toastify } from "react-toastify";
import * as XLSX from "xlsx";
import {
  BsPlus,
  BsEye,
  BsPencil,
  BsTrash,
  BsSearch,
  BsThreeDotsVertical,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
  BsDownload,
  BsInfoCircle,
  BsBarChart,
  BsFunnel,
  BsLightbulb,
  BsClock,
  BsClipboard,
} from "react-icons/bs";
import { useNavigate, useSearchParams } from "react-router-dom";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../../../context/AuthContext";

const USE_SINGLE_MOCK_REQUEST = false;

const getSingleMockRequest = () => [
  {
    _id: "mock-request-001",
    IomGeneratedNumber: "IOM-TEST-0001",
    title: "Mock Office Chairs Purchase",
    description: "Single dummy request for UI testing",
    justification: "Testing procurement list layout and actions",
    department: "Administration",
    category: "Furniture",
    priority: "MEDIUM",
    status: "SUBMITTED",
    branch: "HQ",
    unit: "General Services",
    currentApprover: {
      _id: "mock-approver-1",
      name: "Jane Reviewer",
    },
    updatedBy: {
      _id: "mock-updated-1",
      name: "John Requestor",
    },
    createdBy: {
      _id: "mock-created-1",
      name: "John Requestor",
    },
    createdAt: new Date().toISOString(),
    activeApprovers: ["mock-approver-1"],
  },
];

const RequestList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [currentApproverFilter, setCurrentApproverFilter] = useState("");
  const [lastActionByFilter, setLastActionByFilter] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [currentApproverOptions, setCurrentApproverOptions] = useState([]);
  const [lastActionByOptions, setLastActionByOptions] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });
  const latestRequestIdRef = useRef(0);
  const rejectRemarksRef = useRef("");

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderTextColor = useColorModeValue("gray.700", "gray.100");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const defaultRowHoverBg = useColorModeValue("gray.50", "gray.700");

  const approvalStylesByStatus = {
    APPROVED: {
      bg: useColorModeValue("green.50", "green.900"),
      borderColor: useColorModeValue("green.200", "green.700"),
      hoverBg: useColorModeValue("green.100", "green.800"),
    },
    REJECTED: {
      bg: useColorModeValue("orange.50", "orange.900"),
      borderColor: useColorModeValue("orange.200", "orange.700"),
      hoverBg: useColorModeValue("orange.100", "orange.800"),
    },
    SUBMITTED: {
      bg: useColorModeValue("blue.50", "blue.900"),
      borderColor: useColorModeValue("blue.200", "blue.700"),
      hoverBg: useColorModeValue("blue.100", "blue.800"),
    },
    IN_REVIEW: {
      bg: useColorModeValue("blue.50", "blue.900"),
      borderColor: useColorModeValue("blue.200", "blue.700"),
      hoverBg: useColorModeValue("blue.100", "blue.800"),
    },
    SENT_BACK: {
      bg: useColorModeValue("yellow.50", "yellow.900"),
      borderColor: useColorModeValue("yellow.200", "yellow.700"),
      hoverBg: useColorModeValue("yellow.100", "yellow.800"),
    },
  };

  const statusAccentByStatus = {
    APPROVED: useColorModeValue("green.400", "green.300"),
    REJECTED: useColorModeValue("orange.400", "orange.300"),
    IN_REVIEW: useColorModeValue("blue.400", "blue.300"),
    SUBMITTED: useColorModeValue("blue.400", "blue.300"),
    SENT_BACK: useColorModeValue("yellow.400", "yellow.300"),
  };
  const defaultStatusAccent = useColorModeValue("gray.300", "gray.600");

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const status = searchParams.get("status");
    const priority = searchParams.get("priority");

    if (status) {
      setStatusFilter(status);
    }

    if (priority) {
      setPriorityFilter(priority);
    }
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRequests();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [
    searchTerm,
    statusFilter,
    departmentFilter,
    priorityFilter,
    branchFilter,
    unitFilter,
    currentApproverFilter,
    lastActionByFilter,
  ]);

  const getUniqueSortedValues = (items, field) =>
    [...new Set(items.map((item) => item?.[field]).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );

  const getApprovalStyling = (request) => {
    const isLastApproverPending =
      (request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
      request.currentApprover;

    if (
      (request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
      !isLastApproverPending
    ) {
      return {};
    }

    return approvalStylesByStatus[request.status] || {};
  };

  const getStatusAccent = (status) => {
    return statusAccentByStatus[status] || defaultStatusAccent;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return <BsCheckCircle size={14} />;
      case "REJECTED":
        return <BsXCircle size={14} />;
      case "IN_REVIEW":
      case "SUBMITTED":
        return <BsClock size={14} />;
      case "SENT_BACK":
        return <BsArrowCounterclockwise size={14} />;
      default:
        return null;
    }
  };

  const canActOnRequest = (request) => {
    if (!user) {
      return false;
    }

    if (user.role === "SuperAdmin") {
      return true;
    }

    const activeApproverIds = (request?.activeApprovers || []).map(
      (approver) => approver?._id || approver,
    );

    return (
      activeApproverIds.includes(user._id) ||
      request?.currentApprover?._id === user._id
    );
  };

  const fetchFilterOptions = async () => {
    try {
      if (USE_SINGLE_MOCK_REQUEST) {
        const mockRequests = getSingleMockRequest();
        setDepartmentOptions(getUniqueSortedValues(mockRequests, "department"));
        setBranchOptions(getUniqueSortedValues(mockRequests, "branch"));
        setUnitOptions(getUniqueSortedValues(mockRequests, "unit"));
        setCurrentApproverOptions(
          getUniqueSortedValues(
            mockRequests.map((item) => ({
              currentApproverName: item?.currentApprover?.name,
            })),
            "currentApproverName",
          ),
        );
        setLastActionByOptions(
          getUniqueSortedValues(
            mockRequests.map((item) => ({
              actionByName: item?.updatedBy?.name || item?.createdBy?.name,
            })),
            "actionByName",
          ),
        );
        return;
      }

      const response = await procurementApi.getRequests({
        page: 1,
        limit: 100,
      });
      const requestsData = response?.data || [];
      setDepartmentOptions(getUniqueSortedValues(requestsData, "department"));
      setBranchOptions(getUniqueSortedValues(requestsData, "branch"));
      setUnitOptions(getUniqueSortedValues(requestsData, "unit"));

      // Extract unique current approvers
      const approvers = [
        ...new Set(
          requestsData
            .map((item) => item?.currentApprover?.name)
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b));
      setCurrentApproverOptions(approvers);

      // Extract unique last action by users (updatedBy or createdBy)
      const actionUsers = [
        ...new Set(
          requestsData
            .map((item) => item?.updatedBy?.name || item?.createdBy?.name)
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b));
      setLastActionByOptions(actionUsers);
    } catch (error) {
      console.error("❌ Error fetching filter options:", error);
    }
  };

  const fetchRequests = async () => {
    const requestId = Date.now();
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);

      if (USE_SINGLE_MOCK_REQUEST) {
        const mockRequests = getSingleMockRequest();
        if (latestRequestIdRef.current !== requestId) return;

        setRequests(mockRequests);
        setFilteredRequests(mockRequests);
        setPagination({
          total: 1,
          page: 1,
          limit: 20,
        });
        return;
      }

      const params = {
        page: 1,
        limit: 100,
      };

      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (departmentFilter) params.department = departmentFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (branchFilter?.trim()) params.branch = branchFilter.trim();
      if (unitFilter?.trim()) params.unit = unitFilter.trim();
      if (currentApproverFilter?.trim())
        params.currentApprover = currentApproverFilter.trim();
      if (lastActionByFilter?.trim())
        params.lastActionBy = lastActionByFilter.trim();

      const response = await procurementApi.getRequests(params);
      if (latestRequestIdRef.current !== requestId) return;

      // Handle response structure: { success, message, data, pagination }
      const requestsData = response?.data || [];

      console.log("📦 Fetched requests:", requestsData);
      setRequests(requestsData);
      setFilteredRequests(requestsData);
      setPagination(
        response?.pagination || {
          total: requestsData.length,
          page: 1,
          limit: requestsData.length || 20,
        },
      );
    } catch (error) {
      console.error("❌ Error fetching requests:", error);
      toast({
        title: "Error fetching requests",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (latestRequestIdRef.current !== requestId) return;
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setDepartmentFilter("");
    setPriorityFilter("");
    setBranchFilter("");
    setUnitFilter("");
    setCurrentApproverFilter("");
    setLastActionByFilter("");
  };

  const handleCreateRequest = () => {
    navigate("/procurement/requests/create");
  };

  const handleViewRequest = (id) => {
    navigate(`/procurement/requests/${id}`);
  };

  const handleEditRequest = (id) => {
    navigate(`/procurement/requests/${id}/edit`);
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) {
      return;
    }

    try {
      await procurementApi.deleteRequest(id);
      toast({
        title: "Request deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error deleting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmitRequest = async (id) => {
    try {
      await procurementApi.submitRequest(id);
      toast({
        title: "Request submitted for approval",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleApproveRequest = async (id) => {
    try {
      await procurementApi.approveRequest(id, { comments: "Approved" });
      toast({
        title: "Request approved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error approving request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRejectRequest = (id) => {
    const executeReject = async (toastId) => {
      toastify.dismiss(toastId);
      const remarks = rejectRemarksRef.current?.trim();
      if (!remarks) {
        toastify.error("Please provide a rejection reason.");
        return;
      }

      try {
        await procurementApi.rejectRequest(id, { remarks });
        toastify.success("Request rejected");
        rejectRemarksRef.current = "";
        fetchRequests();
      } catch (error) {
        toastify.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to reject request",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Reason for rejection:
          </Text>
          <Input
            placeholder="Enter rejection remarks"
            size="sm"
            mb={3}
            onChange={(e) => {
              rejectRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => executeReject(toastId)}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                rejectRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/<\/p>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<li>/gi, "\n• ")
      .replace(/<\/li>/gi, "")
      .replace(/<ol>/gi, "")
      .replace(/<\/ol>/gi, "\n")
      .replace(/<ul>/gi, "")
      .replace(/<\/ul>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const handleExportRequests = async () => {
    try {
      const rows = filteredRequests.map((request) => ({
        "IOM Number":
          request.IomGeneratedNumber || request._id?.slice(-8) || "N/A",
        Title: request.title || "",
        Description: stripHtml(request.description),
        Justification: stripHtml(request.justification),
        Department: request.department || "",
        Category: request.category || "",
        Priority: request.priority || "",
        Status: request.status || "",
        "Current Approver": request.currentApprover?.name || "-",
        Branch: request.branch || "",
        Unit: request.unit || "",
        "Requested By": request.createdBy?.name || "",
        "Last Action By":
          request.updatedBy?.name || request.createdBy?.name || "N/A",
        "Created Date": new Date(request.createdAt).toLocaleDateString(),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Requests");
      const filename = `procurement-requests-${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast({
        title: "Export successful",
        description: `Exported ${filteredRequests.length} requests to ${filename}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Failed to export requests",
        description: error.message || "Unable to generate Excel file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const copyTextToClipboard = async (value, label = "content") => {
    const normalizedValue = String(value || "").trim();

    if (
      !normalizedValue ||
      normalizedValue === "N/A" ||
      normalizedValue === "-"
    ) {
      toast({
        title: "Nothing to copy",
        description: `No ${label} available for this request.`,
        status: "info",
        duration: 1800,
        isClosable: true,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(normalizedValue);
      toast({
        title: "Copied",
        description: `${label.charAt(0).toUpperCase() + label.slice(1)} copied to clipboard.`,
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const getRequestCopyText = (request) => {
    const lines = [
      `IOM Number: ${request.IomGeneratedNumber || request._id?.slice(-8) || "N/A"}`,
      `Title: ${request.title || "N/A"}`,
      `Department: ${request.department || "N/A"}`,
      `Category: ${request.category || "N/A"}`,
      `Priority: ${request.priority || "N/A"}`,
      `Status: ${request.status || "N/A"}`,
      `Current Approver: ${request.currentApprover?.name || "-"}`,
      `Last Action By: ${request.updatedBy?.name || request.createdBy?.name || "N/A"}`,
      `Created Date: ${request.createdAt ? new Date(request.createdAt).toLocaleDateString() : "N/A"}`,
    ];

    return lines.join("\n");
  };

  const renderTruncatedCell = (value, options = {}) => {
    const { maxW = "180px", copyLabel = "content" } = options;
    const fullValue = String(value || "N/A").trim() || "N/A";

    return (
      <Tooltip
        label={
          <Box>
            <Text fontSize="sm" fontWeight="500">
              {fullValue}
            </Text>
            <Text fontSize="xs" opacity={0.85}>
              Click to copy
            </Text>
          </Box>
        }
        hasArrow
        placement="top-start"
        openDelay={250}
      >
        <Text
          isTruncated
          maxW={maxW}
          display="block"
          cursor="copy"
          lineHeight="1.35"
          onClick={() => copyTextToClipboard(fullValue, copyLabel)}
        >
          {fullValue}
        </Text>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <HStack spacing={3} align="center">
          <Heading size="lg">Procurement Requests</Heading>
          <IconButton
            icon={<BsInfoCircle />}
            variant="ghost"
            colorScheme="blue"
            onClick={onOpen}
            aria-label="User guidance"
            title="Click for user guidance"
            size="lg"
          />
        </HStack>
        <Button
          leftIcon={<BsPlus />}
          colorScheme="blue"
          onClick={handleCreateRequest}
          w={{ base: "full", md: "auto" }}
        >
          Create Request
        </Button>
      </Flex>

      {/* Filters */}
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={3}
        mb={6}
        flexWrap="wrap"
      >
        <InputGroup maxW={{ base: "full", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="IN_REVIEW">In Review</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="SENT_BACK">Sent Back</option>
          <option value="PO_SELECTED">PO Selected</option>
        </Select>

        <Select
          placeholder="All Departments"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          maxW={{ base: "full", md: "220px" }}
        >
          {departmentOptions.map((department) => (
            <option key={department} value={department}>
              {department}
            </option>
          ))}
        </Select>

        <Select
          placeholder="All Priorities"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          maxW={{ base: "full", md: "150px" }}
        >
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </Select>

        <Select
          placeholder="All Branches"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          {branchOptions.map((branch) => (
            <option key={branch} value={branch}>
              {branch}
            </option>
          ))}
        </Select>

        <Select
          placeholder="All Units"
          value={unitFilter}
          onChange={(e) => setUnitFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          {unitOptions.map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </Select>

        <Select
          placeholder="All Current Approvers"
          value={currentApproverFilter}
          onChange={(e) => setCurrentApproverFilter(e.target.value)}
          maxW={{ base: "full", md: "220px" }}
        >
          {currentApproverOptions.map((approver) => (
            <option key={approver} value={approver}>
              {approver}
            </option>
          ))}
        </Select>

        <Select
          placeholder="All Last Action By"
          value={lastActionByFilter}
          onChange={(e) => setLastActionByFilter(e.target.value)}
          maxW={{ base: "full", md: "220px" }}
        >
          {lastActionByOptions.map((user) => (
            <option key={user} value={user}>
              {user}
            </option>
          ))}
        </Select>

        <Button
          leftIcon={<BsArrowCounterclockwise />}
          variant="outline"
          onClick={handleResetFilters}
          w={{ base: "full", md: "auto" }}
        >
          Reset
        </Button>

        <Button
          leftIcon={<BsDownload />}
          colorScheme="teal"
          onClick={handleExportRequests}
          isDisabled={filteredRequests.length === 0}
          w={{ base: "full", md: "auto" }}
        >
          Export Excel
        </Button>
      </Stack>

      {/* Mobile Cards */}
      <Box display={{ base: "block", md: "none" }}>
        {filteredRequests.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            textAlign="center"
          >
            <Text color="gray.500">No requests found</Text>
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={3}>
            {filteredRequests.map((request) => {
              const approvalStyle = getApprovalStyling(request);
              const statusAccent = getStatusAccent(request.status);

              return (
                <Box
                  key={request._id || request.id}
                  bg={approvalStyle.bg || bgColor}
                  borderWidth="1px"
                  borderColor={approvalStyle.borderColor || borderColor}
                  borderLeft="4px solid"
                  borderLeftColor={statusAccent}
                  borderRadius="lg"
                  p={4}
                  transition="all 0.15s ease"
                >
                  <HStack justify="space-between" align="start" mb={2}>
                    <Box maxW="70%">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {request.IomGeneratedNumber ||
                          request._id?.slice(-8) ||
                          "N/A"}
                      </Text>
                      <Text fontWeight="semibold" noOfLines={2}>
                        {request.title}
                      </Text>
                    </Box>
                    <HStack spacing={1}>
                      {getStatusIcon(request.status)}
                      <StatusBadge status={request.status} />
                    </HStack>
                  </HStack>

                  <Divider my={2} />

                  <SimpleGrid columns={2} spacing={2} mb={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Department
                      </Text>
                      <Text fontSize="sm">{request.department || "N/A"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Category
                      </Text>
                      <Text fontSize="sm">{request.category || "N/A"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Priority
                      </Text>
                      {request.priority ? (
                        <Badge
                          mt={1}
                          colorScheme={
                            request.priority === "URGENT"
                              ? "red"
                              : request.priority === "HIGH"
                                ? "orange"
                                : request.priority === "MEDIUM"
                                  ? "blue"
                                  : "gray"
                          }
                        >
                          {request.priority}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          N/A
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Created
                      </Text>
                      <Text fontSize="sm">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Current Approver
                      </Text>
                      {request.currentApprover?.name ? (
                        <Tag
                          size="sm"
                          colorScheme="blue"
                          borderRadius="full"
                          mt={1}
                        >
                          <TagLabel>{request.currentApprover.name}</TagLabel>
                        </Tag>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          -
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Last Action By
                      </Text>
                      <Text fontSize="sm">
                        {request.updatedBy?.name ||
                          request.createdBy?.name ||
                          "N/A"}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      leftIcon={<BsEye />}
                      variant="outline"
                      onClick={() =>
                        handleViewRequest(request._id || request.id)
                      }
                    >
                      View
                    </Button>

                    {request.status === "DRAFT" && (
                      <>
                        <Button
                          size="sm"
                          leftIcon={<BsPencil />}
                          variant="outline"
                          onClick={() =>
                            handleEditRequest(request._id || request.id)
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          leftIcon={<BsCheckCircle />}
                          colorScheme="green"
                          variant="outline"
                          onClick={() =>
                            handleSubmitRequest(request._id || request.id)
                          }
                        >
                          Submit
                        </Button>
                      </>
                    )}

                    {(request.status === "SUBMITTED" ||
                      request.status === "IN_REVIEW") &&
                      canActOnRequest(request) && (
                        <>
                          <Button
                            size="sm"
                            leftIcon={<BsCheckCircle />}
                            colorScheme="green"
                            variant="outline"
                            onClick={() =>
                              handleApproveRequest(request._id || request.id)
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            leftIcon={<BsXCircle />}
                            colorScheme="red"
                            variant="outline"
                            onClick={() =>
                              handleRejectRequest(request._id || request.id)
                            }
                          >
                            Reject
                          </Button>
                        </>
                      )}

                    {request.status === "DRAFT" && (
                      <Button
                        size="sm"
                        leftIcon={<BsTrash />}
                        colorScheme="red"
                        variant="ghost"
                        onClick={() =>
                          handleDeleteRequest(request._id || request.id)
                        }
                      >
                        Delete
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Box>

      {/* Table */}
      <Box
        display={{ base: "none", md: "block" }}
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
      >
        <Table
          variant="simple"
          tableLayout="fixed"
          w="100%"
          sx={{
            th: { py: 3, whiteSpace: "nowrap" },
            td: { py: 3, verticalAlign: "middle" },
          }}
        >
          <Thead>
            <Tr bg={tableHeaderBg}>
              <Th w="13%" color={tableHeaderTextColor}>
                IOM Number
              </Th>
              <Th w="17%" color={tableHeaderTextColor}>
                Title
              </Th>
              <Th w="12%" color={tableHeaderTextColor}>
                Department
              </Th>
              <Th w="8%" color={tableHeaderTextColor}>
                Priority
              </Th>
              <Th w="10%" color={tableHeaderTextColor}>
                Status
              </Th>
              <Th w="12%" color={tableHeaderTextColor}>
                Current Approver
              </Th>
              <Th w="12%" color={tableHeaderTextColor}>
                Last Action By
              </Th>
              <Th w="8%" color={tableHeaderTextColor}>
                Created Date
              </Th>
              <Th w="8%" color={tableHeaderTextColor}>
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRequests.length === 0 ? (
              <Tr>
                <Td colSpan={9} textAlign="center" py={8}>
                  <Text color="gray.500">No requests found</Text>
                </Td>
              </Tr>
            ) : (
              filteredRequests.map((request) => {
                const approvalStyle = getApprovalStyling(request);
                const statusAccent = getStatusAccent(request.status);
                const isPending =
                  request.status === "SUBMITTED" ||
                  request.status === "IN_REVIEW";

                return (
                  <Tr
                    key={request._id || request.id}
                    bg={approvalStyle.bg}
                    borderLeft="4px solid"
                    borderLeftColor={statusAccent}
                    _hover={{
                      bg: approvalStyle.hoverBg || defaultRowHoverBg,
                      ...(isPending && {
                        transform: "translateY(-1px)",
                        boxShadow: "sm",
                      }),
                    }}
                    transition="all 0.15s ease"
                  >
                    <Td fontWeight="semibold" fontSize="sm">
                      {renderTruncatedCell(
                        request.IomGeneratedNumber ||
                          request._id?.slice(-8) ||
                          "N/A",
                        {
                          maxW: "210px",
                          copyLabel: "iom number",
                        },
                      )}
                    </Td>
                    <Td maxW="260px">
                      {renderTruncatedCell(request.title, {
                        maxW: "240px",
                        copyLabel: "title",
                      })}
                    </Td>
                    <Td>
                      {renderTruncatedCell(request.department, {
                        maxW: "160px",
                        copyLabel: "department",
                      })}
                    </Td>
                    <Td>
                      {request.priority ? (
                        <Badge
                          colorScheme={
                            request.priority === "URGENT"
                              ? "red"
                              : request.priority === "HIGH"
                                ? "orange"
                                : request.priority === "MEDIUM"
                                  ? "blue"
                                  : "gray"
                          }
                        >
                          {request.priority}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          N/A
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        {getStatusIcon(request.status)}
                        <StatusBadge status={request.status} />
                      </HStack>
                    </Td>
                    <Td fontSize="sm">
                      {renderTruncatedCell(
                        request.currentApprover?.name || "-",
                        {
                          maxW: "170px",
                          copyLabel: "current approver",
                        },
                      )}
                    </Td>
                    <Td fontSize="sm">
                      {renderTruncatedCell(
                        request.updatedBy?.name ||
                          request.createdBy?.name ||
                          "N/A",
                        {
                          maxW: "170px",
                          copyLabel: "last action by",
                        },
                      )}
                    </Td>
                    <Td whiteSpace="nowrap">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </Td>
                    <Td whiteSpace="nowrap">
                      <HStack spacing={1}>
                        <IconButton
                          size="sm"
                          icon={<BsEye />}
                          variant="ghost"
                          onClick={() =>
                            handleViewRequest(request._id || request.id)
                          }
                          aria-label="View request"
                        />
                        {request.status === "DRAFT" && (
                          <IconButton
                            size="sm"
                            icon={<BsPencil />}
                            variant="ghost"
                            onClick={() =>
                              handleEditRequest(request._id || request.id)
                            }
                            aria-label="Edit request"
                          />
                        )}
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<BsThreeDotsVertical />}
                            size="sm"
                            variant="ghost"
                          />
                          <Portal>
                            <MenuList>
                              {request.status === "DRAFT" && (
                                <>
                                  <MenuItem
                                    icon={<BsCheckCircle />}
                                    onClick={() =>
                                      handleSubmitRequest(
                                        request._id || request.id,
                                      )
                                    }
                                  >
                                    Submit for Approval
                                  </MenuItem>
                                  <MenuItem
                                    icon={<BsTrash />}
                                    onClick={() =>
                                      handleDeleteRequest(
                                        request._id || request.id,
                                      )
                                    }
                                    color="red.500"
                                  >
                                    Delete
                                  </MenuItem>
                                </>
                              )}
                              {(request.status === "SUBMITTED" ||
                                request.status === "IN_REVIEW") &&
                                canActOnRequest(request) && (
                                  <>
                                    <MenuItem
                                      icon={<BsCheckCircle />}
                                      onClick={() =>
                                        handleApproveRequest(
                                          request._id || request.id,
                                        )
                                      }
                                    >
                                      Approve
                                    </MenuItem>
                                    <MenuItem
                                      icon={<BsXCircle />}
                                      onClick={() =>
                                        handleRejectRequest(
                                          request._id || request.id,
                                        )
                                      }
                                      color="red.500"
                                    >
                                      Reject
                                    </MenuItem>
                                  </>
                                )}
                              <MenuItem
                                icon={<BsClipboard />}
                                onClick={() =>
                                  copyTextToClipboard(
                                    getRequestCopyText(request),
                                    "row details",
                                  )
                                }
                              >
                                Copy Complete Row
                              </MenuItem>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Summary */}
      <Flex justify="space-between" align="center" mt={4}>
        <Text color={mutedTextColor}>
          Showing {filteredRequests.length} of{" "}
          {pagination.total || requests.length} requests
        </Text>
      </Flex>

      {/* User Guidance Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Procurement Request Guidance</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Stack spacing={4}>
              <Box>
                <HStack spacing={2} mb={2}>
                  <BsBarChart size={20} color="currentColor" />
                  <Text fontWeight="bold">Row Status Visual Hierarchy</Text>
                </HStack>
                <Stack spacing={3}>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={2}
                    >
                      BACKGROUND TINT (Workflow State)
                    </Text>
                    <Stack spacing={2}>
                      <HStack align="start">
                        <Box
                          w={4}
                          h={4}
                          bg="green.100"
                          borderColor="green.200"
                          borderWidth="1px"
                          borderRadius="md"
                          flexShrink={0}
                        />
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            Green (Approved)
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Request completed and approved by all required
                            approvers.
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box
                          w={4}
                          h={4}
                          bg="orange.100"
                          borderColor="orange.200"
                          borderWidth="1px"
                          borderRadius="md"
                          flexShrink={0}
                        />
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            Orange (Rejected)
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Request was rejected and may need revision or
                            resubmission.
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box
                          w={4}
                          h={4}
                          bg="blue.100"
                          borderColor="blue.200"
                          borderWidth="1px"
                          borderRadius="md"
                          flexShrink={0}
                        />
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            Blue (Pending Approval)
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Request is awaiting approval. Check "Current
                            Approver" field.
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box
                          w={4}
                          h={4}
                          bg="yellow.100"
                          borderColor="yellow.200"
                          borderWidth="1px"
                          borderRadius="md"
                          flexShrink={0}
                        />
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            Yellow (Sent Back)
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Requires revision or additional information before
                            resubmission.
                          </Text>
                        </Box>
                      </HStack>
                    </Stack>
                  </Box>
                  <Divider my={2} />
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={2}
                    >
                      LEFT ACCENT BORDER (Urgency & Actionability)
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Colored left border matches the row background. Visible at
                      a glance even when scrolling through dense request lists.
                      Same color system as background tint.
                    </Text>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsCheckCircle size={20} color="currentColor" />
                  <Text fontWeight="bold">Status Icons & Pills</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>Each request displays an icon next to its status:</Text>
                  <Stack spacing={1} pl={4}>
                    <Text>
                      • <strong>✓ (Checkmark):</strong> Approved — completed
                      workflow
                    </Text>
                    <Text>
                      • <strong>✕ (X):</strong> Rejected — action required
                    </Text>
                    <Text>
                      • <strong>⏱ (Clock):</strong> Pending — awaiting approver
                      decision
                    </Text>
                    <Text>
                      • <strong>↻ (Arrow):</strong> Sent Back — revision needed
                    </Text>
                  </Stack>
                  <Text pt={2}>
                    These icons provide instant visual confirmation of status
                    before reading the text pill.
                  </Text>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsInfoCircle size={20} color="currentColor" />
                  <Text fontWeight="bold">Current Approver Highlight</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    The current approver is displayed in a{" "}
                    <strong>blue pill-shaped tag</strong> to emphasize who needs
                    to act next.
                  </Text>
                  <Text color="blue.700" fontWeight="semibold">
                    Example: Jane Smith
                  </Text>
                  <Text>
                    This creates instant accountability visibility. Approvers
                    can quickly identify their pending items.
                  </Text>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsArrowCounterclockwise size={20} color="currentColor" />
                  <Text fontWeight="bold">Actionable Rows</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    Rows with <strong>pending approval</strong> (Submitted or In
                    Review status) lift up slightly when you hover over them,
                    signaling that action is available.
                  </Text>
                  <Text>
                    Completed rows (Approved, Rejected) do not lift,
                    distinguishing actionable items from historical records.
                  </Text>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsFunnel size={20} color="currentColor" />
                  <Text fontWeight="bold">Filtering & Search</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    • <strong>Search:</strong> Find requests by title,
                    description, or ID
                  </Text>
                  <Text>
                    • <strong>Status:</strong> Filter by Draft, Submitted, In
                    Review, Approved, Rejected, etc.
                  </Text>
                  <Text>
                    • <strong>Department:</strong> View requests from specific
                    departments
                  </Text>
                  <Text>
                    • <strong>Priority:</strong> Filter by Low, Medium, High, or
                    Urgent
                  </Text>
                  <Text>
                    • <strong>Current Approver:</strong> See requests assigned
                    to specific approvers
                  </Text>
                  <Text>
                    • <strong>Reset:</strong> Clear all filters to view all
                    requests
                  </Text>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsDownload size={20} color="currentColor" />
                  <Text fontWeight="bold">Export & Actions</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    • <strong>Export Excel:</strong> Download filtered requests
                    as an Excel file
                  </Text>
                  <Text>
                    • <strong>View:</strong> Open request details
                  </Text>
                  <Text>
                    • <strong>Edit:</strong> Modify draft requests only
                  </Text>
                  <Text>
                    • <strong>Approve/Reject:</strong> Available for pending
                    requests in your review queue
                  </Text>
                  <Text>
                    • <strong>Menu (...):</strong> Additional actions like
                    submit, delete, and more
                  </Text>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={2}>
                  <BsLightbulb size={20} color="currentColor" />
                  <Text fontWeight="bold">Tips</Text>
                </HStack>
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    • Use multiple filters together for precise searches
                  </Text>
                  <Text>• Export Excel files for reporting and archiving</Text>
                  <Text>
                    • Check the "Current Approver" field (blue pill) to know who
                    needs to act next
                  </Text>
                  <Text>
                    • Look for rows that lift on hover — those are items
                    awaiting your approval
                  </Text>
                  <Text>
                    • Mobile view shows cards; desktop view shows table for
                    better scanning
                  </Text>
                </Stack>
              </Box>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RequestList;
